import React, { useState, useEffect, useMemo } from 'react';
import { publicFetch } from '../utils/publicApi';
import './PublicAvailability.css';
import PersonTeamLists from '../components/PersonTeamLists';
import PersonTimeOff from '../components/PersonTimeOff';

const API_BASE = 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

export default function PublicAvailability({ token, embedded = false }) {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const [personName, setPersonName] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [invalidToken, setInvalidToken] = useState(false);

  // Your own Team Assignments and Team Exclusions: they save as you add or remove them.
  const teamApi = useMemo(() => {
    const base = `${API_BASE}/public/availability/${token}/teams`;
    const fail = async (res, fallback) => {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || fallback);
    };
    return {
      load: async () => {
        const res = await publicFetch(base, { credentials: 'include' });
        if (!res.ok) await fail(res, 'Could not load your teams');
        return res.json();
      },
      add: async (kind, teamName, division) => {
        const res = await publicFetch(base, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ kind, teamName, division })
        });
        if (!res.ok) await fail(res, 'Could not save');
      },
      remove: async (kind, id) => {
        const res = await publicFetch(`${base}/${kind}/${id}`, { method: 'DELETE', credentials: 'include' });
        if (!res.ok) await fail(res, 'Could not remove');
      }
    };
  }, [token]);

  // Your own time off: saved as you add or remove it.
  const timeOffApi = useMemo(() => {
    const base = `${API_BASE}/public/availability/${token}/time-off`;
    const fail = async (res, fallback) => {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || fallback);
    };
    return {
      load: async () => {
        const res = await publicFetch(base, { credentials: 'include' });
        if (!res.ok) await fail(res, 'Could not load your time off');
        return res.json();
      },
      add: async (body) => {
        const res = await publicFetch(base, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body)
        });
        const data = await res.json().catch(() => ({}));
        if (res.status === 409 && data.needsConfirmation) return data;
        if (!res.ok) throw new Error(data.error || 'Could not save');
        return data;
      },
      remove: async (id) => {
        const res = await publicFetch(`${base}/${id}`, { method: 'DELETE', credentials: 'include' });
        if (!res.ok) await fail(res, 'Could not remove');
      }
    };
  }, [token]);

  useEffect(() => {
    const loadAvailability = async () => {
      try {
        const res = await publicFetch(`${API_BASE}/public/availability/${token}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setPersonName(data.firstName || data.fullName || '');
          setEffectiveFrom(data.effectiveFrom || '');

          const availMap = {};
          if (data.availability && data.availability.length > 0) {
            data.availability.forEach(item => {
              availMap[item.dayOfWeek] = {
                startTime: item.startTime,
                endTime: item.endTime,
                isAvailable: item.isAvailable
              };
            });
          }
          // Fill in any missing days with defaults
          for (let i = 0; i < 7; i++) {
            if (!availMap[i]) {
              availMap[i] = { startTime: '06:00', endTime: '23:00', isAvailable: true };
            }
          }
          setAvailability(availMap);
        } else if (res.status === 404) {
          setInvalidToken(true);
        } else {
          setError('Failed to load availability');
        }
      } catch (err) {
        setError('Error loading availability: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadAvailability();
  }, [token]);

  const handleTimeChange = (dayOfWeek, field, value) => {
    setAvailability(prev => ({
      ...prev,
      [dayOfWeek]: {
        ...prev[dayOfWeek],
        [field]: value
      }
    }));
  };

  const handleAvailableChange = (dayOfWeek, value) => {
    setAvailability(prev => ({
      ...prev,
      [dayOfWeek]: {
        ...prev[dayOfWeek],
        isAvailable: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const availabilityArray = [];
      for (let i = 0; i < 7; i++) {
        const dayAvail = availability[i] || { startTime: '06:00', endTime: '23:00', isAvailable: true };
        availabilityArray.push({
          dayOfWeek: i,
          startTime: dayAvail.startTime,
          endTime: dayAvail.endTime,
          isAvailable: dayAvail.isAvailable
        });
      }

      const res = await publicFetch(`${API_BASE}/public/availability/${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ availability: availabilityArray })
      });

      if (res.ok) {
        setSuccess('Availability saved successfully!');
        setTimeout(() => setSuccess(''), 5000);
      } else {
        const errorData = await res.json();
        setError(errorData.error || 'Failed to save availability');
      }
    } catch (err) {
      setError('Error saving availability: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const formatEffectiveDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-AU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    if (embedded) {
      return <div className="public-tab-loading">Loading your availability...</div>;
    }
    return (
      <div className="public-availability">
        <div className="public-availability-card">
          <div className="public-loading">Loading your availability...</div>
        </div>
      </div>
    );
  }

  if (invalidToken) {
    if (embedded) {
      return <div className="public-tab-error">Failed to load availability data.</div>;
    }
    return (
      <div className="public-availability">
        <div className="public-availability-card">
          <div className="public-header">
            <h1>Availability Update</h1>
          </div>
          <div className="public-invalid-token">
            <h2>Invalid or Expired Link</h2>
            <p>This availability link is no longer valid. Please contact your administrator for a new link.</p>
          </div>
        </div>
      </div>
    );
  }

  const availabilityContent = (
    <>
      {!embedded && (
        <div className="public-header">
          <h1>Update Your Availability</h1>
          {personName && <p className="public-greeting">Hi {personName}!</p>}
        </div>
      )}

      {effectiveFrom && (
        <div className="effective-from-banner">
          <span className="effective-from-label">Changes take effect from:</span>
          <span className="effective-from-date">{formatEffectiveDate(effectiveFrom)}</span>
        </div>
      )}

      {error && <div className="public-error">{error}</div>}
      {success && <div className="public-success">{success}</div>}

      <p className="public-instructions">
        Set your available start and end times for each day of the week.
        You will only be rostered during the times you mark as available.
      </p>

      <div className="public-availability-grid">
        <table className="public-availability-table">
          <thead>
            <tr>
              <th>Day</th>
              <th>Available</th>
              <th>Start Time</th>
              <th>End Time</th>
            </tr>
          </thead>
          <tbody>
            {days.map((day, index) => (
              <tr key={index} className={availability[index]?.isAvailable === false ? 'unavailable-row' : ''}>
                <td className="day-name">{day}</td>
                <td className="available-cell">
                  <input
                    type="checkbox"
                    checked={availability[index]?.isAvailable !== false}
                    onChange={(e) => handleAvailableChange(index, e.target.checked)}
                  />
                </td>
                <td>
                  <input
                    type="time"
                    value={availability[index]?.startTime || '06:00'}
                    onChange={(e) => handleTimeChange(index, 'startTime', e.target.value)}
                    disabled={availability[index]?.isAvailable === false}
                  />
                </td>
                <td>
                  <input
                    type="time"
                    value={availability[index]?.endTime || '23:00'}
                    onChange={(e) => handleTimeChange(index, 'endTime', e.target.value)}
                    disabled={availability[index]?.isAvailable === false}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="public-actions">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-save-availability"
        >
          {saving ? 'Saving...' : 'Save Availability'}
        </button>
      </div>

      <div className="public-team-lists" style={{ marginTop: '1.5rem' }}>
        <PersonTimeOff api={timeOffApi} />
        <PersonTeamLists api={teamApi} />
      </div>

      {!embedded && (
        <div className="public-footer">
          <p>SwishIQ</p>
        </div>
      )}
    </>
  );

  if (embedded) {
    return availabilityContent;
  }

  return (
    <div className="public-availability">
      <div className="public-availability-card">
        {availabilityContent}
      </div>
    </div>
  );
}
